/**
 * project_management#545 — attribute GraphQL errors to report sections.
 *
 * The sequence report loads with `errorPolicy: 'all'`, so a failed field
 * arrives as `data.…analysis.<field> = null` plus an entry in `errors[]`
 * whose `path` ends in that field. This maps those paths onto the sections
 * the page renders, so a single failed section can be replaced by a notice
 * while everything else still renders (in the browser and in the PDF).
 */

export type ReportSection =
  | 'interpretations'
  | 'hrv'
  | 'respiration'
  | 'apnea'
  | 'episodes'
  | 'ectopy'
  | 'qtc'
  | 'extremesHR'
  | 'clusters';

/** `Analysis` fields each section depends on (order = display order). */
export const REPORT_SECTION_FIELDS: Record<ReportSection, readonly string[]> = {
  interpretations: ['riskAssessment', 'overallInterpretation'],
  hrv: ['hrvTimeSeries', 'hrvInterpretation'],
  respiration: ['respiration', 'respirationTimeSeries'],
  apnea: ['apnea', 'apneaTimeSeries'],
  episodes: [
    'afibs',
    'bradycardiaEpisodes',
    // project_management#561: the drawn chunks of the charted bradycardia episodes.
    'bradycardiaExcerpts',
    'tachycardiaEpisodes',
    'pauseEpisodes',
    'afibInterpretation',
    'bradycardiaInterpretation',
    'tachycardiaInterpretation',
  ],
  ectopy: [
    'ventricularDuplets',
    'ventricularTriplets',
    'ventricularRuns',
    'supraVentricularDuplets',
    'supraVentricularTriplets',
    'supraVentricularRuns',
    'ectopyInterpretation',
  ],
  qtc: ['sequenceIntervals', 'qtcInterpretation'],
  extremesHR: ['extremesByHeartRate'],
  clusters: ['clusterData'],
};

export const REPORT_SECTIONS = Object.keys(REPORT_SECTION_FIELDS) as ReportSection[];

/** The subset of a GraphQL error this module needs (works for ApolloError.graphQLErrors). */
export interface ReportGraphQLError {
  message: string;
  path?: ReadonlyArray<string | number> | null;
}

export interface ReportErrors {
  /** Errors that could not be tied to an `analysis` field (auth, not found, root failures). */
  other: string[];
  /** First error message per failed `analysis` field. */
  byField: Record<string, string>;
  /** Sections with at least one failed field, in display order. */
  failedSections: ReportSection[];
  /** Message for a section, or null when the section loaded. */
  forSection(section: ReportSection): string | null;
  /** True when anything failed — the report is partial. */
  hasErrors: boolean;
}

/** The `analysis` sub-field an error path points at, if any. */
export function analysisFieldOf(path: ReadonlyArray<string | number> | null | undefined): string | null {
  if (!path) return null;

  const index = path.indexOf('analysis');

  if (index === -1) return null;

  const field = path[index + 1];

  return typeof field === 'string' ? field : null;
}

export interface CollectOptions {
  /**
   * A query that produced NO data at all (network error, server 5xx): every
   * section that depends on it is failed with this message.
   */
  chartUnavailable?: string | null;
}

export function collectReportErrors(
  errors: ReadonlyArray<ReportGraphQLError>,
  options: CollectOptions = {},
): ReportErrors {
  const byField: Record<string, string> = {};
  const other: string[] = [];

  errors.forEach((error) => {
    const field = analysisFieldOf(error.path);

    if (field) {
      if (!byField[field]) byField[field] = error.message;
    } else if (!other.includes(error.message)) {
      other.push(error.message);
    }
  });

  if (options.chartUnavailable) {
    REPORT_SECTIONS.forEach((section) => {
      REPORT_SECTION_FIELDS[section].forEach((field) => {
        if (!byField[field]) byField[field] = options.chartUnavailable as string;
      });
    });
  }

  const forSection = (section: ReportSection): string | null => {
    const failedField = REPORT_SECTION_FIELDS[section].find((field) => byField[field]);

    return failedField ? byField[failedField] : null;
  };

  const failedSections = REPORT_SECTIONS.filter((section) => forSection(section) !== null);

  return {
    other,
    byField,
    failedSections,
    forSection,
    hasErrors: failedSections.length > 0 || other.length > 0,
  };
}

export type ReportStatus = 'ok' | 'partial' | 'error';

/** Value for `data-report-status` on `#pdf-ready` — read by the PDF generator. */
export function reportStatusOf(reportErrors: ReportErrors): ReportStatus {
  return reportErrors.hasErrors ? 'partial' : 'ok';
}
